import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import CustomerChat from '../components/CustomerChat';
import './CustomerPortal.css';

function CustomerDashboard() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [customer, setCustomer] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cars');
  const [showCarForm, setShowCarForm] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carForm, setCarForm] = useState({
    make: '',
    model: '',
    year: '',
    licensePlate: '',
    mileage: '',
    vin: '',
    color: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    const storedCustomer = localStorage.getItem('customer');

    if (!token || !storedCustomer) {
      navigate('/customer-login');
      return;
    }

    setCustomer(JSON.parse(storedCustomer));
    loadCars();
  }, []);

  const loadCars = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      const tenantId = localStorage.getItem('customerTenantId');
      
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      if (tenantId && tenantId !== 'null') {
        headers['X-Tenant-ID'] = tenantId;
      }
      
      const response = await fetch(`${config.API_URL || '/api'}/customer-cars`, {
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('customerToken');
          localStorage.removeItem('customer');
          localStorage.removeItem('customerTenantId');
          navigate('/customer-login');
          return;
        }
        throw new Error('Fehler beim Laden der Autos');
      }

      const data = await response.json();
      setCars(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading cars:', error);
      showNotification('Fehler beim Laden der Autos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('customerToken');
      const tenantId = localStorage.getItem('customerTenantId');
      
      const carData = {
        ...carForm,
        year: carForm.year ? parseInt(carForm.year) : null,
        mileage: carForm.mileage ? parseInt(carForm.mileage) : null
      };

      const url = editingCar 
        ? `${config.API_URL || '/api'}/customer-cars/${editingCar.id}`
        : `${config.API_URL || '/api'}/customer-cars`;
      
      const method = editingCar ? 'PUT' : 'POST';

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(carData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Speichern');
      }

      showNotification(editingCar ? 'Auto aktualisiert' : 'Auto hinzugefügt', 'success');
      setShowCarForm(false);
      setEditingCar(null);
      setCarForm({
        make: '',
        model: '',
        year: '',
        licensePlate: '',
        mileage: '',
        vin: '',
        color: ''
      });
      loadCars();
    } catch (error) {
      console.error('Error saving car:', error);
      showNotification(error.message || 'Fehler beim Speichern', 'error');
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Möchten Sie dieses Auto wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('customerToken');
      const tenantId = localStorage.getItem('customerTenantId');
      
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
      }
      
      const response = await fetch(`${config.API_URL || '/api'}/customer-cars/${carId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen');
      }

      showNotification('Auto gelöscht', 'success');
      loadCars();
    } catch (error) {
      console.error('Error deleting car:', error);
      showNotification('Fehler beim Löschen', 'error');
    }
  };

  const handleEditCar = (car) => {
    setEditingCar(car);
    setCarForm({
      make: car.make || '',
      model: car.model || '',
      year: car.year || '',
      licensePlate: car.licensePlate || '',
      mileage: car.mileage || '',
      vin: car.vin || '',
      color: car.color || ''
    });
    setShowCarForm(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customer');
    localStorage.removeItem('customerTenantId');
    navigate('/customer-login');
  };

  if (loading) {
    return <div className="customer-portal-loading"><Spinner /></div>;
  }

  if (!customer) {
    return null;
  }

  const customerToken = localStorage.getItem('customerToken');

  return (
    <div className="customer-portal">
      <div className="customer-portal-header">
        <div>
          <h1>Mein Kundenportal</h1>
          <p>Willkommen, {customer.firstName} {customer.lastName}</p>
        </div>
        <button onClick={handleLogout} className="btn-secondary" style={{ marginLeft: 'auto' }}>
          Abmelden
        </button>
      </div>

      <div className="customer-portal-tabs">
        <button
          className={activeTab === 'cars' ? 'active' : ''}
          onClick={() => setActiveTab('cars')}
        >
          Meine Autos
        </button>
        <button
          className={activeTab === 'chat' ? 'active' : ''}
          onClick={() => setActiveTab('chat')}
        >
          Nachricht
        </button>
      </div>

      <div className="customer-portal-content">
        {activeTab === 'cars' && (
          <div className="portal-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Meine Autos</h2>
              <button
                className="btn-primary"
                onClick={() => navigate('/customer-cars/new/edit')}
              >
                Neues Auto hinzufügen
              </button>
            </div>

            {showCarForm && (
              <div className="appointment-form-modal">
                <div className="modal-content">
                  <h3>{editingCar ? 'Auto bearbeiten' : 'Neues Auto'}</h3>
                  <form onSubmit={handleCarSubmit}>
                    <div className="form-group">
                      <label>Marke *</label>
                      <input
                        type="text"
                        value={carForm.make}
                        onChange={(e) => setCarForm({ ...carForm, make: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Modell *</label>
                      <input
                        type="text"
                        value={carForm.model}
                        onChange={(e) => setCarForm({ ...carForm, model: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Baujahr</label>
                      <input
                        type="number"
                        value={carForm.year}
                        onChange={(e) => setCarForm({ ...carForm, year: e.target.value })}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                      />
                    </div>
                    <div className="form-group">
                      <label>Kennzeichen</label>
                      <input
                        type="text"
                        value={carForm.licensePlate}
                        onChange={(e) => setCarForm({ ...carForm, licensePlate: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Kilometerstand</label>
                      <input
                        type="number"
                        value={carForm.mileage}
                        onChange={(e) => setCarForm({ ...carForm, mileage: e.target.value })}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>FIN</label>
                      <input
                        type="text"
                        value={carForm.vin}
                        onChange={(e) => setCarForm({ ...carForm, vin: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Farbe</label>
                      <input
                        type="text"
                        value={carForm.color}
                        onChange={(e) => setCarForm({ ...carForm, color: e.target.value })}
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingCar ? 'Aktualisieren' : 'Hinzufügen'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setShowCarForm(false);
                          setEditingCar(null);
                        }}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {cars.length === 0 ? (
              <p>Noch keine Autos vorhanden. Fügen Sie Ihr erstes Auto hinzu!</p>
            ) : (
              <div className="cars-list">
                {cars.map(car => (
                  <div key={car.id} className="car-card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/customer-cars/${car.id}`)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <h3>{car.make} {car.model} {car.year}</h3>
                        {car.licensePlate && <p>Kennzeichen: {car.licensePlate}</p>}
                        {car.mileage && <p>Kilometerstand: {car.mileage.toLocaleString('de-DE')} km</p>}
                        {car.vin && <p>FIN: {car.vin}</p>}
                        {car.color && <p>Farbe: {car.color}</p>}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn-secondary"
                          onClick={() => navigate(`/customer-cars/${car.id}/edit`)}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleDeleteCar(car.id)}
                          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && customer.tenantId && (
          <div className="portal-section">
            <CustomerChat 
              customerId={customer.id} 
              tenantId={customer.tenantId}
              useCustomerAuth={true}
            />
          </div>
        )}
        {activeTab === 'chat' && !customer.tenantId && (
          <div className="portal-section">
            <p>Chat ist nur für Kunden verfügbar, die einer Werkstatt zugeordnet sind.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDashboard;
